import { apiFetchInit, apiUrl } from "@/lib/apiOrigin";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Mail, Search, UserPlus } from "lucide-react";
import toast from "react-hot-toast";
import { AtwProductMark } from "@/components/Brand/AtwProductMark";
import { atwMarkIdForSystem } from "@/components/Brand/atwProductMarks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDirectoryState } from "@/components/Directory/DirectoryContext";
import { buildSystemAccessDraft, visibleSystems } from "@/components/Directory/SystemAccessPanel";
import { directoryDisplayName, directoryRoleLabel } from "@/components/Directory/roleLabels";
import { directoryHref } from "@/lib/atwDirectoryHost";
import { CreateUserDialog } from "@/components/Users/CreateUserDialog";
import {
  AdminStepUpDialog,
  postUserAdminAction,
  type AdminStepUpCredentials,
} from "@/components/Users/AdminStepUpDialog";
import {
  mailboxStatusLabel,
  type DirectoryMailboxStatus,
} from "@/lib/directoryMailboxLabels";
import type { GlobalAppSlug } from "@/lib/dashboardAppRegistry";
import { hasWorkspaceSystemAccess, type AtwStoredSystemId } from "@/lib/atwSystemAccess";

type InviteFilter = "all" | "invite_pending" | "verified";

type Person = {
  id: string;
  email?: string | null;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role: string;
  isActive: boolean;
  jobTitle?: string | null;
  accountDomain?: string | null;
  appEntitlements?: GlobalAppSlug[] | null;
  systemAccess?: AtwStoredSystemId[] | null;
  inviteDeliveryEmail?: string | null;
  verificationState?: "missing_email" | "invite_pending" | "verified";
  mailboxStatus?: DirectoryMailboxStatus;
  mailboxEmail?: string | null;
};

const PAGE_SIZE = 100;

function hasMailGrant(
  person: Person,
  orgId: string | null | undefined,
  orgMailEnabled: boolean,
): boolean {
  const draft = buildSystemAccessDraft({
    role: person.role,
    appEntitlements: person.appEntitlements,
    systemAccess: person.systemAccess,
    orgId,
    orgMailEnabled,
  });
  return hasWorkspaceSystemAccess(draft.systemAccess);
}

export function PeoplePage() {
  const state = useDirectoryState();
  const ctx = state.status === "ready" ? state.value : null;
  const [people, setPeople] = useState<Person[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get("q") || "");
  const inviteParam = searchParams.get("invite");
  const inviteFilter: InviteFilter =
    inviteParam === "invite_pending" || inviteParam === "verified" ? inviteParam : "all";
  const setInviteFilter = (id: InviteFilter) => {
    const next = new URLSearchParams(searchParams);
    if (id === "all") next.delete("invite");
    else next.set("invite", id);
    setSearchParams(next, { replace: true });
  };
  const [createOpen, setCreateOpen] = useState(false);
  const [resendUserId, setResendUserId] = useState<string | null>(null);
    
  const domain = ctx?.org?.domain || null;

  const load = useCallback(
    async (skip: number) => {
      if (!domain) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({
          accountDomain: domain,
          limit: String(PAGE_SIZE),
          skip: String(skip),
          includeMailbox: "1",
        });
        const res = await fetch(apiUrl(`/api/users?${params.toString()}`), { ...apiFetchInit(), cache: "no-store" });
        const data = await res.json();
        if (!res.ok) {
          toast.error(data?.error || "Could not load people.");
          return;
        }
        const rows = (data.users || []) as Person[];
        setPeople((prev) => (skip === 0 ? rows : [...prev, ...rows]));
        setTotal(Number(data.pagination?.total) || rows.length);
      } catch {
        toast.error("Could not load people.");
      } finally {
        setLoading(false);
      }
    },
    [domain],
  );

  useEffect(() => {
    if (ctx?.canManageUsers) void load(0);
  }, [ctx?.canManageUsers, load]);

  useEffect(() => {
    setQuery(searchParams.get("q") || "");
  }, [searchParams]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people.filter((p) => {
      if (inviteFilter === "invite_pending") {
        if (p.verificationState !== "invite_pending" && p.verificationState !== "missing_email") {
          return false;
        }
      } else if (inviteFilter === "verified") {
        if (p.verificationState !== "verified") return false;
      }
      if (!q) return true;
      return (
        (p.email || "").toLowerCase().includes(q) ||
        directoryDisplayName(p).toLowerCase().includes(q) ||
        (p.jobTitle || "").toLowerCase().includes(q)
      );
    });
  }, [people, query, inviteFilter]);

  const pendingCount = useMemo(
    () =>
      people.filter(
        (p) =>
          p.verificationState === "invite_pending" || p.verificationState === "missing_email",
      ).length,
    [people],
  );

  if (!ctx) return null;

  if (!ctx.canManageUsers) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">People</h1>
        <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-100">
          This session is signed in as{" "}
          <span className="font-semibold">{directoryRoleLabel(ctx.me.role)}</span>
          {ctx.org?.companyName || ctx.org?.domain
            ? ` in ${ctx.org.companyName || ctx.org.domain}`
            : ""}
          . The roster, invites, Requests, Groups, and Security stay locked until your role in this
          organization is Organization Owner or Account Admin. Open the account menu (avatar) to
          confirm the active organization, or open your own profile below.
        </p>
        {ctx.me?.id ? (
          <Link
            to={directoryHref(`/people/${encodeURIComponent(ctx.me.id)}`)}
            className="inline-flex h-9 items-center rounded-md bg-gray-900 px-3 text-sm font-semibold text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900"
          >
            Your profile
          </Link>
        ) : null}
      </div>
    );
  }

  const systems = visibleSystems(ctx.assignableAppSlugs);
  const orgMailEnabled = ctx.org?.mailEnabled === true;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {inviteFilter === "invite_pending" ? "Invites" : "People"}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Everyone in {ctx.org?.companyName || domain}. Invite status, mailbox readiness, and which
            ATW systems each person can open.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <UserPlus className="mr-2 size-4" aria-hidden />
          Add person
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search
            className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
          <Input
            placeholder="Search people"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Invite status filter">
          {(
            [
              { id: "all" as const, label: "All" },
              {
                id: "invite_pending" as const,
                label: pendingCount ? `Invites (${pendingCount})` : "Invites",
              },
              { id: "verified" as const, label: "Signed in" },
            ] as const
          ).map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setInviteFilter(opt.id)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                inviteFilter === opt.id
                  ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                  : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-white/15 dark:bg-[#1D232D] dark:text-gray-300 dark:hover:bg-white/5"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-[#1D232D]">
        {loading && people.length === 0 ? (
          <div className="flex justify-center py-12">
            <Loader2 className="size-6 animate-spin text-gray-400" aria-label="Loading" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-12 text-center text-sm text-gray-500">No people found.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-white/10">
            {filtered.map((person) => {
              const draft = buildSystemAccessDraft({
                role: person.role,
                appEntitlements: person.appEntitlements,
                systemAccess: person.systemAccess,
                orgId: ctx.org?.orgId,
                orgMailEnabled,
              });
              const open = systems.filter((s) =>
                s.storage.kind === "systemAccess"
                  ? draft.systemAccess.includes(s.id as AtwStoredSystemId)
                  : draft.apps.includes(s.storage.slug as Exclude<GlobalAppSlug, "home">),
              );
              const mailGranted = hasMailGrant(person, ctx.org?.orgId, orgMailEnabled);
              const canResend =
                person.role !== "super_admin" || ctx.isSuperAdmin
                  ? person.verificationState === "invite_pending" ||
                    person.verificationState === "missing_email"
                  : false;

              return (
                <li key={person.id}>
                  <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/5">
                    <Link to={directoryHref(`/people/${encodeURIComponent(person.id)}`)}
                      className="min-w-0 flex-1"
                    >
                      <p className="truncate font-medium text-gray-900 dark:text-white">
                        {directoryDisplayName(person)}
                      </p>
                      <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                        {person.email || "—"}
                        {person.jobTitle ? ` · ${person.jobTitle}` : ""}
                      </p>
                    </Link>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="secondary">{directoryRoleLabel(person.role)}</Badge>
                      {person.verificationState === "missing_email" ? (
                        <Badge variant="outline">Needs login email</Badge>
                      ) : person.verificationState === "invite_pending" ? (
                        <Badge variant="outline">Invite pending</Badge>
                      ) : person.isActive === false ? (
                        <Badge variant="outline">Inactive</Badge>
                      ) : null}
                      {mailGranted ? (
                        <Badge
                          variant="outline"
                          className="font-normal"
                          title={person.mailboxEmail || person.email || undefined}
                        >
                          {mailboxStatusLabel(person.mailboxStatus || "none")}
                        </Badge>
                      ) : null}
                      {open.map((s) => {
                        const markId = atwMarkIdForSystem(s.id);
                        return (
                          <Badge
                            key={s.id}
                            variant="outline"
                            className="gap-1.5 font-normal"
                          >
                            {markId ? (
                              <AtwProductMark
                                id={markId}
                                size={14}
                                className="dark:brightness-0 dark:invert"
                              />
                            ) : null}
                            {s.name}
                          </Badge>
                        );
                      })}
                      {canResend ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1 px-2 text-xs"
                          onClick={() => setResendUserId(person.id)}
                        >
                          <Mail className="size-3.5" aria-hidden />
                          Resend invite
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {people.length < total ? (
        <Button variant="outline" disabled={loading} onClick={() => void load(people.length)}>
          {loading ? "Loading…" : "Load more"}
        </Button>
      ) : null}

      {domain ? (
        <CreateUserDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          accountDomain={domain}
          onSuccess={() => void load(0)}
        />
      ) : null}

      <AdminStepUpDialog
        open={resendUserId !== null}
        onOpenChange={(open) => {
          if (!open) setResendUserId(null);
        }}
        title="Resend invite"
        description="Confirm your password to resend the organization invite email."
        confirmLabel="Send invite"
        onConfirm={async (creds: AdminStepUpCredentials) => {
          if (!resendUserId) return { error: "No user selected." };
          const result = await postUserAdminAction(resendUserId, "resend_invite", creds);
          if ("needsMfa" in result && result.needsMfa) return result;
          if ("error" in result && result.error) return { error: result.error };
          if (!("ok" in result) || !result.ok) return { error: "Request failed." };
          toast.success("Invite resent.");
          setResendUserId(null);
          void load(0);
          return { ok: true };
        }}
      />
</div>
  );
}
